import React from 'react';
import styled from '@emotion/styled';
import { ThumbsUp } from 'lucide-react';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../../dashboardTokens';
import { ClampedText } from './summariesShared';
import { groupByPivot } from './solutionGroups';

// Vipupisteet and Ratkaisut summary views (design guidance,
// specs/workshop-dashboard.md): votes show when voting is in use, and the
// facilitator's curation is always visible.

const PivotGrid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  height: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(clamp(120px, 12vmin, 280px), 1fr));
  grid-auto-rows: 1fr;
  gap: clamp(6px, 0.9vmin, 20px);
  min-height: 0;
  overflow: hidden;
`;

const PivotCard = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  min-height: 0;
  padding: clamp(8px, 1vmin, 24px);
  border-radius: clamp(8px, 1vmin, 22px);
  font-size: ${DASH_TYPE.meta};
  border: 1px solid ${({ $selected }) => ($selected ? DASH_COLORS.accent : DASH_COLORS.tileBorder)};
  background: ${({ $selected }) => ($selected ? DASH_COLORS.accentSoft : DASH_COLORS.wash)};

  footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    font-family: ${DASH_FONTS.numeric};
    font-weight: 700;
    color: ${DASH_COLORS.textDim};
  }
  .selected { color: ${DASH_COLORS.accent}; font-family: inherit; font-weight: 600; }
`;

function PivotsTile({ t, summary }) {
  return (
    <PivotGrid>
      {summary.breakdown.map((pivot) => (
        <PivotCard key={pivot.key} $selected={pivot.selected}>
          <ClampedText lines={3} title={pivot.label || undefined}>{pivot.label}</ClampedText>
          <footer>
            <span className="selected">{pivot.selected ? `✓ ${t('dashboard.selected')}` : ''}</span>
            {summary.votingUsed && <span>{pivot.votes}</span>}
          </footer>
        </PivotCard>
      ))}
    </PivotGrid>
  );
}

const SolutionCloud = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
  gap: clamp(6px, 0.9vmin, 20px);
  min-height: 0;
  overflow: hidden;
`;

// A solution is a round-cornered rectangle: small when it carries only a
// title and a sentence or two, larger with more content (decision
// 2026-10-19). Backend orders by votes, then content — the flex-wrap keeps
// that order. On a crowded tile, solutions without votes shrink further so
// the voted ones keep the room.
const SOLUTION_TIERS = [
  { minChars: 800, width: 'clamp(200px, 20vmin, 440px)', lines: 2 },
  { minChars: 240, width: 'clamp(160px, 16vmin, 350px)', lines: 2 },
  { minChars: 0, width: 'clamp(120px, 12vmin, 260px)', lines: 1 },
];
const MINI_TIER = { width: 'clamp(90px, 9vmin, 180px)', lines: 1 };
const CROWDED_AT = 12;

function solutionTier(sol, { crowded, detail }) {
  if (!detail && crowded && !sol.votes) return MINI_TIER;
  return SOLUTION_TIERS.find((tier) => (sol.contentChars || 0) >= tier.minChars);
}

const SolutionCard = styled.li`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
  padding: clamp(8px, 1vmin, 24px);
  border-radius: clamp(8px, 1vmin, 20px);
  border: 1px solid ${DASH_COLORS.accent};
  background: ${DASH_COLORS.accentSoft};
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.text};
`;

const VoteChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
  align-self: flex-end;
  font-family: ${DASH_FONTS.numeric};
  font-weight: 700;
  color: ${DASH_COLORS.textDim};

  svg { flex: 0 0 auto; }
`;

// Solutions sit under the pivot point they answer (#1431), one heading per
// pivot; with no pivots at all there is nothing to head, so no headings.
const PivotGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(8px, 1.2vmin, 28px);
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const GroupHeading = styled.h3`
  margin: 0 0 clamp(4px, 0.6vmin, 12px);
  font-size: ${DASH_TYPE.meta};
  font-weight: 700;
  color: ${DASH_COLORS.textDim};
`;

function SolutionsCards({ t, summary, detail = false }) {
  const crowded = summary.breakdown.length > CROWDED_AT;
  const groups = groupByPivot(summary.breakdown);
  const headed = groups.some((g) => g.key);
  return (
    <PivotGroups>
      {groups.map((group) => (
        <section key={group.key || 'none'}>
          {headed && (
            <GroupHeading>
              <ClampedText lines={1} title={group.label || undefined}>{group.label || t('dashboard.noPivot')}</ClampedText>
            </GroupHeading>
          )}
          <SolutionCloud>
            {group.solutions.map((sol) => {
              const tier = solutionTier(sol, { crowded, detail });
              return (
                <SolutionCard
                  key={sol.key}
                  style={{ width: tier.width }}
                  title={(sol.label || '').slice(0, 200)}
                >
                  <ClampedText lines={detail ? tier.lines + 1 : tier.lines}>{sol.label}</ClampedText>
                  {summary.votingUsed && sol.votes > 0 && (
                    <VoteChip><ThumbsUp size={14} /> {sol.votes}</VoteChip>
                  )}
                </SolutionCard>
              );
            })}
          </SolutionCloud>
        </section>
      ))}
    </PivotGroups>
  );
}

function SolutionsDetail(props) {
  return <SolutionsCards {...props} detail />;
}

export const pivotPointsView = { Tile: PivotsTile, Detail: PivotsTile };
export const solutionsView = { Tile: SolutionsCards, Detail: SolutionsDetail };
